import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Clock, MapPin, Users, CheckCircle, Ban, AlertCircle, CalendarPlus } from "lucide-react";
import { useLang } from "@/LanguageContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDT(dt, lang) {
  if (!dt) return "";
  const d = new Date(dt.replace(" ", "T"));
  return d.toLocaleString(lang === "de" ? "de-DE" : "en-GB", {
    weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  }) + (lang === "de" ? " Uhr" : "");
}

export default function EventDetailPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const tx = t[lang];
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", persons_count: 1 });
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const formTs = useRef(Math.floor(Date.now() / 1000));

  const load = () => {
    axios.get(`${API}/events/${id}?lang=${lang}`)
      .then(({ data }) => setEvent(data))
      .catch((err) => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "VMBS – " + (lang === "de" ? "Veranstaltung" : "Event");
    load();
    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      const { data } = await axios.post(`${API}/events/${id}/register`, { ...form, _website: honeypot, _ts: formTs.current });
      setSuccess({ is_waitlist: data.is_waitlist, message: data.message });
      // refresh seat count in background
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : (lang === "de" ? "Anmeldung fehlgeschlagen." : "Registration failed."));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C1620] text-white">
        <Header />
        <div className="pt-24 flex justify-center"><div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }
  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-[#0C1620] text-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 pt-32 pb-16 text-center">
          <AlertCircle className="w-12 h-12 text-[#C5993A] mx-auto mb-4" />
          <h1 className="font-heading text-2xl mb-2">{lang === "de" ? "Veranstaltung nicht gefunden" : "Event not found"}</h1>
          <Link to="/#veranstaltungen" className="inline-flex items-center gap-2 text-[#C5993A] hover:underline mt-4">
            <ArrowLeft className="w-4 h-4" />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const seatsExhausted = event.max_attendees > 0 && event.seats_left === 0;
  const canRegister = !event.is_cancelled && (!seatsExhausted || event.waitlist_enabled);

  return (
    <div className="min-h-screen bg-[#0C1620] text-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
        <Link
          to="/#veranstaltungen"
          data-testid="back-to-events"
          className="inline-flex items-center gap-2 text-[#C5993A] hover:text-[#E5BC65] text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
        </Link>

        <div className="space-y-6">
          <header>
            <h1 data-testid="event-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-light mb-3">{event.title}</h1>
            {event.is_cancelled && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 text-sm">
                <Ban className="w-4 h-4" />
                <span><b>{lang === "de" ? "Storniert" : "Cancelled"}</b>{event.cancel_reason ? ` – ${event.cancel_reason}` : ""}</span>
              </div>
            )}
          </header>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Info icon={Clock} label={lang === "de" ? "Beginn" : "Start"} value={formatDT(event.start_at, lang)} />
            {event.end_at && <Info icon={Clock} label={lang === "de" ? "Ende" : "End"} value={formatDT(event.end_at, lang)} />}
            {event.location && <Info icon={MapPin} label={lang === "de" ? "Ort" : "Location"} value={event.location} />}
            {event.min_attendees > 0 && <Info icon={Users} label={lang === "de" ? "Mindestteilnehmer" : "Min. attendees"} value={String(event.min_attendees)} />}
            {event.show_capacity && event.seats_left !== null && event.seats_left !== undefined && (
              <Info icon={Users}
                label={lang === "de" ? "Freie Plätze" : "Seats left"}
                value={String(event.seats_left ?? 0)}
              />
            )}
          </dl>

          {event.description_html && (
            <article
              data-testid="event-description"
              className="prose prose-invert max-w-none text-[#F0F4F8] event-body"
              dangerouslySetInnerHTML={{ __html: event.description_html }}
            />
          )}

          {/* iCal download */}
          <a
            href={`${API}/events/${event.id}.ics?lang=${lang}`}
            data-testid="ical-download"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C5993A]/40 text-[#C5993A] rounded-md hover:bg-[#C5993A]/10 transition-colors text-sm"
          >
            <CalendarPlus className="w-4 h-4" />
            {lang === "de" ? "Termin in Kalender speichern (.ics)" : "Add to calendar (.ics)"}
          </a>
        </div>

        {/* Registration */}
        <div className="mt-10 border-t border-[#3A4F60]/40 pt-8">
          <h2 className="font-heading text-2xl text-white mb-4">{lang === "de" ? "Anmeldung" : "Registration"}</h2>

          {!canRegister ? (
            <p className="text-[#8899AA]">
              {event.is_cancelled
                ? (lang === "de" ? "Diese Veranstaltung wurde storniert. Eine Anmeldung ist nicht mehr möglich." : "This event has been cancelled. Registration is no longer possible.")
                : (lang === "de" ? "Diese Veranstaltung ist ausgebucht." : "This event is fully booked.")}
            </p>
          ) : success ? (
            <div data-testid="reg-success" className="bg-[#12202f] border border-[#C5993A]/40 rounded-lg p-6 flex items-start gap-4">
              <CheckCircle className="w-7 h-7 text-[#C5993A] flex-shrink-0" />
              <div>
                <p className="text-white font-medium">
                  {success.is_waitlist
                    ? (lang === "de" ? "Sie wurden auf die Warteliste gesetzt." : "You have been added to the waitlist.")
                    : (lang === "de" ? "Anmeldung erfolgreich. Eine Bestätigung wurde per E-Mail versendet." : "Registration successful. A confirmation has been sent by email.")}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                <input type="text" name="_website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "de" ? "Name *" : "Name *"}>
                  <Input data-testid="reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#12202f] border-[#3A4F60]/50 text-white" />
                </Field>
                <Field label={lang === "de" ? "E-Mail *" : "Email *"}>
                  <Input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#12202f] border-[#3A4F60]/50 text-white" />
                </Field>
                <Field label={lang === "de" ? "Telefon" : "Phone"}>
                  <Input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#12202f] border-[#3A4F60]/50 text-white" />
                </Field>
                <Field label={lang === "de" ? "Ich komme mit … Personen" : "I'm coming with … persons"}>
                  <select data-testid="reg-persons" value={form.persons_count}
                    onChange={(e) => setForm({ ...form, persons_count: parseInt(e.target.value) })}
                    className="w-full bg-[#12202f] border border-[#3A4F60]/50 text-white rounded h-10 px-3">
                    {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label={lang === "de" ? "Nachricht (optional)" : "Message (optional)"}>
                <Textarea data-testid="reg-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[#12202f] border-[#3A4F60]/50 text-white" />
              </Field>
              {error && <p className="text-red-400 text-sm" data-testid="reg-error">{error}</p>}
              {seatsExhausted && event.waitlist_enabled && (
                <p className="text-amber-300 text-sm">{lang === "de" ? "Veranstaltung ist ausgebucht. Sie werden auf die Warteliste gesetzt." : "Event is fully booked. You will be placed on the waitlist."}</p>
              )}
              <Button type="submit" data-testid="reg-submit" disabled={sending}
                className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
                {sending ? (lang === "de" ? "Wird gesendet…" : "Sending…") : (lang === "de" ? "Anmelden" : "Register")}
              </Button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
      <Icon className="w-4 h-4 text-[#C5993A] mt-0.5 flex-shrink-0" />
      <div>
        <dt className="text-[10px] uppercase tracking-wider text-[#8899AA]">{label}</dt>
        <dd className="text-white text-sm mt-0.5">{value}</dd>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#8899AA] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
